/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import { AudioTrackInfo } from 'components/organisms/gen-ai-audio-tool/GenAITool';
import EmptyPlaybackCard from './variations/EmptyPlaybackCard';
import LoadingPlaybackCard from './variations/LoadingPlaybackCard';
import ReadyPlaybackCard from './variations/ReadyPlaybackCard';
import ResultPlaybackCard from './variations/ResultPlaybackCard';
import CTAPlaybackCard from './variations/CTAPlaybackCard';

interface AudioScriptPlaybackCardProps {
  audioUrl?: string;
  script?: string;
  isLoading?: boolean;
  generatedAt?: Date;
  handlePlayAudio: (
    trackInfo: Pick<AudioTrackInfo, 'id' | 'audioElement'> & {
      playFromStart?: boolean;
    },
  ) => void;
  handlePauseAudio: () => void;
  isPlaying: boolean;
  handleAudioEnded: () => void;
}

enum PlaybackCardState {
  EMPTY = 'empty',
  READY = 'ready',
  LOADING = 'loading',
  RESULT = 'result',
  CTA = 'cta',
}

const calculateMinutes = (timestamp: Date) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  return Math.floor(diffMs / 60000);
};

export const AudioScriptPlaybackCard: React.FC<AudioScriptPlaybackCardProps> = ({
  audioUrl,
  script,
  isLoading = false,
  generatedAt,
  handlePlayAudio,
  handlePauseAudio,
  isPlaying,
  handleAudioEnded,
}) => {
  const [showCTA, setShowCTA] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [minutesSinceGeneration, setMinutesSinceGeneration] = useState(0);

  // Access form state using react-final-form's useForm hook
  const form = useForm();
  const formState = form?.getState();

  const playbackCardState = useMemo(() => {
    if (isLoading) {
      setShowCTA(false);
      return PlaybackCardState.LOADING;
    }
    if (showCTA) return PlaybackCardState.CTA;
    if (script && audioUrl) return PlaybackCardState.RESULT;
    if (formState.valid) return PlaybackCardState.READY;

    return PlaybackCardState.EMPTY;
  }, [isLoading, script, audioUrl, formState.valid, showCTA]);

  const handlePlayPause = () => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      handlePauseAudio();
    } else {
      handlePlayAudio({
        id: 'script',
        audioElement: audioRef.current,
      });
      setShowCTA(false);
    }
  };

  const restartAudio = () => {
    if (!audioRef.current || !audioUrl) return;
    handlePlayAudio({
      id: 'script',
      audioElement: audioRef.current,
      playFromStart: true,
    });
    setShowCTA(false);
  };

  const handleEnded = () => {
    handleAudioEnded();
    setShowCTA(true);
  };

  useEffect(() => {
    if (!generatedAt) return;

    setMinutesSinceGeneration(0);

    const interval = setInterval(() => {
      setMinutesSinceGeneration(calculateMinutes(generatedAt));
    }, 60000);

    /* eslint-disable-next-line consistent-return */
    return () => clearInterval(interval);
  }, [generatedAt]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={audioUrl} onEnded={handleEnded}>
        <track kind="captions" src="" label="English" />
      </audio>
      {playbackCardState === PlaybackCardState.EMPTY && <EmptyPlaybackCard />}
      {playbackCardState === PlaybackCardState.LOADING && (
        <LoadingPlaybackCard />
      )}
      {playbackCardState === PlaybackCardState.READY && <ReadyPlaybackCard />}
      {playbackCardState === PlaybackCardState.CTA && (
        <CTAPlaybackCard
          minutesSinceGeneration={minutesSinceGeneration}
          restartAudio={() => restartAudio()}
        />
      )}
      {playbackCardState === PlaybackCardState.RESULT &&
        !!script &&
        !!audioUrl && (
          <ResultPlaybackCard
            minutesSinceGeneration={minutesSinceGeneration}
            script={script}
            onPlayPause={handlePlayPause}
            audioUrl={audioUrl}
            isPlaying={isPlaying}
          />
        )}
    </>
  );
};

export default AudioScriptPlaybackCard;
